// React hooks that subscribe to the Firestore data behind the Live Map
import { useEffect, useMemo, useRef, useState } from 'react';

import { compact, flatten, omit } from 'lodash';

import { firestore } from '@isd/lib/firebase';
import { Users } from '@isd/lib/firebase/db/collections';
import { JobAssignmentDoc } from '@isd/lib/firebase/db/metaTypes';
import {
  geotracksByUserBetweenQuery,
  jobsAssignmentsLeafNodesByJobQuery,
  recentGeotracksByUserQuery,
} from '@isd/lib/firebase/db/queries';

import { getEndOfDay, getStartOfDay } from '@isd/lib/helpers/dates';
import log from '@isd/lib/logger';

import { useAuthContext } from '@/context/AuthContext';

import { toGeotrackPoint } from '@/components/LiveMap/liveMapAdapters';
import {
  GeotrackPoint,
  TruckLocation,
  buildTruckLocation,
} from '@/components/LiveMap/liveMapModel';

// Number of recent fixes kept per truck: enough to derive a stable heading and
// draw a short tail showing where the truck has just been.
const RECENT_GEOTRACKS_LIMIT = 20;

/**
 * Listen to the leaf-node JobAssignments (the operators actually driving) of
 * every given job. Listeners are added and removed per job as the list changes,
 * so the assignments already loaded stay put (and the trucks stay on the map)
 * when a job is added or filtered out.
 * @param {string[]} jobIds
 * @returns {{ assignments: JobAssignmentDoc[], isLoading: boolean }}
 */
export function useJobsAssignments(jobIds: string[]): {
  assignments: JobAssignmentDoc[];
  isLoading: boolean;
} {
  const { userDoc } = useAuthContext();
  const [assignmentsByJobId, setAssignmentsByJobId] = useState<
    Record<string, JobAssignmentDoc[]>
  >({});
  const unsubscribersRef = useRef<Record<string, () => void>>({});
  const subscribedUserIdRef = useRef<string | null>(null);
  const jobIdsKey = [...jobIds].sort().join(',');
  const userId = userDoc?.id ?? null;

  useEffect(() => {
    // Another signed-in user can see different assignments, so start over
    if (subscribedUserIdRef.current !== userId) {
      Object.values(unsubscribersRef.current).forEach((unsubscribe) =>
        unsubscribe()
      );
      unsubscribersRef.current = {};
      subscribedUserIdRef.current = userId;
      setAssignmentsByJobId({});
    }
    if (!userDoc) return;
    const unsubscribers = unsubscribersRef.current;
    const wantedJobIds = new Set(jobIds);
    // Drop listeners for jobs no longer listed, and their assignments in one update
    const droppedJobIds = Object.keys(unsubscribers).filter(
      (jobId) => !wantedJobIds.has(jobId)
    );
    droppedJobIds.forEach((jobId) => {
      unsubscribers[jobId]?.();
      delete unsubscribers[jobId];
    });
    if (droppedJobIds.length) {
      setAssignmentsByJobId((previous) => omit(previous, droppedJobIds));
    }
    // Add listeners for new jobs
    wantedJobIds.forEach((jobId) => {
      if (unsubscribers[jobId]) return;
      unsubscribers[jobId] = firestore.onSnapshot(
        jobsAssignmentsLeafNodesByJobQuery({ jobId, userDoc }),
        (querySnapshot) => {
          const docs = firestore.filterSoftDeletedDocs(querySnapshot.docs);
          setAssignmentsByJobId((previous) => ({
            ...previous,
            [jobId]: docs,
          }));
        }
      );
    });
  }, [jobIdsKey, userId]);

  // Tear everything down on unmount
  useEffect(() => {
    return () => {
      Object.values(unsubscribersRef.current).forEach((unsubscribe) =>
        unsubscribe()
      );
      unsubscribersRef.current = {};
    };
  }, []);

  // Only the listed jobs, so a job just removed doesn't count for the render
  // before its listener is dropped
  const assignments = useMemo(
    () => flatten(jobIds.map((jobId) => assignmentsByJobId[jobId] || [])),
    [assignmentsByJobId, jobIdsKey]
  );
  // Loading only until the first jobs report, so adding a job (e.g. clearing
  // a filter) doesn't swap the whole pane back to placeholders
  const isLoading =
    jobIds.length > 0 && jobIds.every((jobId) => !assignmentsByJobId[jobId]);
  return { assignments, isLoading };
}

/**
 * Keep one live listener per operator on their most recent Geotrack fixes and
 * expose the derived position and heading. Listeners are added and removed
 * incrementally as the operator list changes. Fixes from before today are
 * ignored so a phone that stopped reporting yesterday doesn't show up as live.
 * @param {string[]} uids - Operator user ids.
 * @param {boolean} enabled - Pass false to tear down every listener (e.g. while simulating).
 * @returns {Record<string, TruckLocation>} Keyed by operator uid.
 */
export function useFirestoreTruckLocations(
  uids: string[],
  enabled: boolean
): Record<string, TruckLocation> {
  const [locations, setLocations] = useState<Record<string, TruckLocation>>({});
  const unsubscribersRef = useRef<Record<string, () => void>>({});
  const uidsKey = [...uids].sort().join(',');

  useEffect(() => {
    const wantedUids = new Set(enabled ? uids : []);
    const unsubscribers = unsubscribersRef.current;
    // Drop listeners for operators no longer shown, and their locations in one update
    const droppedUids = Object.keys(unsubscribers).filter(
      (uid) => !wantedUids.has(uid)
    );
    droppedUids.forEach((uid) => {
      unsubscribers[uid]?.();
      delete unsubscribers[uid];
    });
    if (droppedUids.length) {
      setLocations((previous) => omit(previous, droppedUids));
    }
    // Add listeners for new operators
    const startOfToday = getStartOfDay().getTime();
    wantedUids.forEach((uid) => {
      if (unsubscribers[uid]) return;
      unsubscribers[uid] = firestore.onSnapshot(
        recentGeotracksByUserQuery({ uid, limitTo: RECENT_GEOTRACKS_LIMIT }),
        (querySnapshot) => {
          const points = compact(
            querySnapshot.docs.map((doc) => toGeotrackPoint(doc.data()))
          ).filter((point) => point.timestamp >= startOfToday);
          const location = buildTruckLocation(uid, points);
          setLocations(({ [uid]: _previous, ...rest }) =>
            location ? { ...rest, [uid]: location } : rest
          );
        }
      );
    });
  }, [uidsKey, enabled]);

  // Tear everything down on unmount
  useEffect(() => {
    const unsubscribers = unsubscribersRef.current;
    return () => {
      Object.values(unsubscribers).forEach((unsubscribe) => unsubscribe());
      unsubscribersRef.current = {};
    };
  }, []);

  return locations;
}

export type OperatorProfile = {
  avatarUrl: string | null;
  isTracking: boolean | null; // Location sharing switch in the mobile app
  phoneNumber: string | null;
};

// Profile fields read from the user document that the @isd/lib types don't list
type UserProfileFields = {
  avatar?: { origin?: string };
  isTracking?: boolean;
  phoneNumber?: string;
};

const EMPTY_PROFILE: OperatorProfile = {
  avatarUrl: null,
  isTracking: null,
  phoneNumber: null,
};

// One read per operator per page load, shared by markers and the truck popup
const profileRequests: Record<string, Promise<OperatorProfile>> = {};

/**
 * Read an operator's avatar, tracking switch and phone number from their user
 * document, caching the request.
 * @param {string} uid
 * @returns {Promise<OperatorProfile>}
 */
export function loadOperatorProfile(uid: string): Promise<OperatorProfile> {
  profileRequests[uid] =
    profileRequests[uid] ||
    firestore
      .getDoc(Users.doc(uid))
      .then((userDoc) => {
        const data = userDoc.data() as UserProfileFields | undefined;
        return {
          avatarUrl: data?.avatar?.origin || null,
          isTracking:
            typeof data?.isTracking === 'boolean' ? data.isTracking : null,
          phoneNumber: data?.phoneNumber || null,
        };
      })
      .catch((error) => {
        log.error(`Could not load the profile of operator ${uid}`, error);
        delete profileRequests[uid];
        return EMPTY_PROFILE;
      });
  return profileRequests[uid];
}

/**
 * Profiles (avatar, tracking switch, phone) for a set of operators.
 * @param {string[]} uids - Real operator ids (not simulated ones).
 * @returns {Record<string, OperatorProfile>} Keyed by uid; missing while loading.
 */
export function useOperatorProfiles(
  uids: string[]
): Record<string, OperatorProfile> {
  const [profiles, setProfiles] = useState<Record<string, OperatorProfile>>({});
  const uidsKey = [...uids].sort().join(',');
  useEffect(() => {
    let isCancelled = false;
    Promise.all(
      uids.map((uid) =>
        loadOperatorProfile(uid).then((profile) => [uid, profile] as const)
      )
    ).then((entries) => {
      if (!isCancelled) {
        setProfiles((previous) => ({
          ...previous,
          ...Object.fromEntries(entries),
        }));
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [uidsKey]);
  return profiles;
}

// Cap on how many fixes are pulled for the selected truck's route
const TRAIL_MAX_POINTS = 1500;

/**
 * Today's full route for one operator, kept live while selected. Used to draw
 * the lanes a truck is running and to work out which site it last left.
 * @param {string | null} uid - Selected operator, or null for none.
 * @param {boolean} enabled - Pass false when the data comes from elsewhere (simulation).
 * @returns {GeotrackPoint[]} Fixes ordered oldest to newest.
 */
export function useFirestoreTruckTrail(
  uid: string | null,
  enabled: boolean
): GeotrackPoint[] {
  const [trail, setTrail] = useState<GeotrackPoint[]>([]);
  useEffect(() => {
    setTrail([]);
    if (!uid || !enabled) return;
    const unsubscribe = firestore.onSnapshot(
      geotracksByUserBetweenQuery({
        uid,
        sinceMs: getStartOfDay().getTime(),
        untilMs: getEndOfDay().getTime(),
        limitTo: TRAIL_MAX_POINTS,
      }),
      (querySnapshot) => {
        const points = compact(
          querySnapshot.docs.map((doc) => toGeotrackPoint(doc.data()))
        ).reverse();
        setTrail(points);
      }
    );
    return unsubscribe;
  }, [uid, enabled]);
  return trail;
}

// A day's fixes per operator, read once per page load (today's live tail tops them up)
const historyRequests: Record<string, Promise<GeotrackPoint[]>> = {};

/**
 * Read an operator's fixes for one day, oldest to newest, caching the request
 * so re-selecting a job doesn't read them again.
 * @param {string} uid
 * @param {Date} day - Any time on the day to read.
 * @returns {Promise<GeotrackPoint[]>}
 */
export function loadTruckHistory(
  uid: string,
  day: Date
): Promise<GeotrackPoint[]> {
  const sinceMs = getStartOfDay(day).getTime();
  const untilMs = getEndOfDay(day).getTime();
  const key = `${uid}:${sinceMs}`;
  historyRequests[key] =
    historyRequests[key] ||
    firestore
      .getDocs(
        geotracksByUserBetweenQuery({
          uid,
          sinceMs,
          untilMs,
          limitTo: TRAIL_MAX_POINTS,
        })
      )
      .then((querySnapshot) =>
        compact(
          querySnapshot.docs.map((doc) => toGeotrackPoint(doc.data()))
        ).reverse()
      )
      .catch((error) => {
        log.error(`Could not load the GPS history of operator ${uid}`, error);
        delete historyRequests[key];
        return [];
      });
  return historyRequests[key];
}

/**
 * A day's fixes for a set of operators. Used to draw the routes trucks drove
 * on past days and the site-to-site legs of jobs with more than two sites.
 * @param {string[]} uids - Real operator ids (not simulated ones).
 * @param {Date} day - The scheduler's day.
 * @returns {Record<string, GeotrackPoint[]>} Keyed by uid; missing while loading.
 */
export function useTruckHistories(
  uids: string[],
  day: Date
): Record<string, GeotrackPoint[]> {
  const [histories, setHistories] = useState<Record<string, GeotrackPoint[]>>(
    {}
  );
  const dayStartMs = getStartOfDay(day).getTime();
  const uidsKey = [...uids].sort().join(',');
  const loadedDayRef = useRef(dayStartMs);
  useEffect(() => {
    let isCancelled = false;
    // Start over on a new day; when trucks are only added or removed, keep the
    // routes already drawn so they don't blink while the (cached) loads resolve
    if (loadedDayRef.current !== dayStartMs) {
      loadedDayRef.current = dayStartMs;
      setHistories({});
    }
    Promise.all(
      uids.map((uid) =>
        loadTruckHistory(uid, day).then((history) => [uid, history] as const)
      )
    ).then((entries) => {
      if (!isCancelled) {
        setHistories((previous) => ({
          ...previous,
          ...Object.fromEntries(entries),
        }));
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [uidsKey, dayStartMs]);
  return histories;
}
